import React, { useState } from "react";
import { Phone, Copy, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

/**
 * Formats a phone number for tel: links (strips non-digits except leading +)
 */
const formatPhoneForTel = (phone) => {
  if (!phone) return null;
  // Keep leading + if present, strip everything else except digits
  const cleaned = phone.replace(/[^\d+]/g, "");
  // Ensure we have something meaningful
  return cleaned.length >= 7 ? cleaned : null;
};

/**
 * Detects if user is on a touch device (mobile/tablet)
 * More reliable than just checking userAgent - checks for touch capability
 */
const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  );
};

/**
 * Stores pending call info in localStorage for post-call prompt
 */
const storePendingCall = (contactId, contactName) => {
  if (!contactId) return;
  const pendingCall = {
    contact_id: contactId,
    contact_name: contactName,
    started_at: new Date().toISOString(),
  };
  localStorage.setItem("pendingCall", JSON.stringify(pendingCall));
};

/**
 * CallButton - A phone dialer action component
 * 
 * Props:
 * - phone: The phone number to call
 * - contactId: ID of the contact (for post-call logging)
 * - contactName: Name of the contact (for accessibility & logging)
 * - variant: "primary" (full button) | "icon" (icon only) | "icon-label" (icon + small label)
 * - size: "default" | "sm" | "lg"
 * - className: Additional classes
 * - label: Custom label text (default: "Call" or "Call now")
 * - onCallInitiated: Callback when a call is initiated (optional)
 */
const CallButton = ({
  phone,
  contactId,
  contactName = "contact",
  variant = "primary",
  size = "default",
  className = "",
  label,
  onCallInitiated,
  "data-testid": testId,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const formattedPhone = formatPhoneForTel(phone);
  const hasPhone = !!formattedPhone;
  const isTouch = isTouchDevice();

  const handleCallClick = (e) => {
    // Store pending call for post-call prompt
    storePendingCall(contactId, contactName);
    
    // Notify parent if callback provided
    if (onCallInitiated) {
      onCallInitiated({ contactId, contactName, phone: formattedPhone });
    }
  };

  const handleCopyNumber = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (phone) {
      navigator.clipboard.writeText(phone);
      toast.success("Phone number copied!");
    }
    setMenuOpen(false);
  };

  const handleDialAnyway = (e) => {
    e.stopPropagation();
    // Store pending call for post-call prompt
    storePendingCall(contactId, contactName);
    
    if (onCallInitiated) {
      onCallInitiated({ contactId, contactName, phone: formattedPhone });
    }
    
    if (formattedPhone) {
      window.location.href = `tel:${formattedPhone}`;
    }
    setMenuOpen(false);
  };

  // Button styling based on variant
  const buttonClasses = `${
    variant === "primary"
      ? "bg-[#81B29A] hover:bg-[#71A28A] text-white"
      : "border-[#F4F1EA] hover:bg-[#F4F1EA]"
  } ${variant === "icon" ? "px-2" : ""} rounded-full ${className}`;

  const disabledButtonClasses = `opacity-50 cursor-not-allowed ${
    variant === "primary" ? "bg-[#81B29A] text-white" : "border-[#F4F1EA]"
  } ${variant === "icon" ? "px-2" : ""} rounded-full ${className}`;

  // No phone number - show disabled button with tooltip
  if (!hasPhone) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">
              <Button
                variant={variant === "primary" ? "default" : "outline"}
                size={size}
                disabled
                className={disabledButtonClasses}
                data-testid={testId}
              >
                <Phone className={`w-4 h-4 ${variant !== "icon" ? "mr-1" : ""}`} />
                {variant !== "icon" && (label || "Call")}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="bg-[#3D405B] text-white rounded-xl">
            <p>No phone number</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Touch device - simple <a> tag with tel: link, NO dropdown, NO preventDefault
  // This ensures maximum iOS/Android reliability
  if (isTouch) {
    return (
      <a
        href={`tel:${formattedPhone}`}
        onClick={(e) => {
          e.stopPropagation();
          handleCallClick(e);
          // Let the default tel: behavior happen - don't preventDefault!
        }}
        aria-label={`Call ${contactName}`}
        className={`inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 ${buttonClasses} ${
          size === "sm" ? "h-8 px-3 text-xs" : size === "lg" ? "h-10 px-8" : "h-9 px-4 py-2"
        } ${variant === "icon" ? "!px-2" : ""}`}
        data-testid={testId}
      >
        <Phone className={`w-4 h-4 ${variant !== "icon" ? "mr-1" : ""}`} />
        {variant !== "icon" && (label || "Call")}
      </a>
    );
  }

  // Desktop - show dropdown with copy + dial options
  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button
          variant={variant === "primary" ? "default" : "outline"}
          size={size}
          className={buttonClasses}
          data-testid={testId}
        >
          <Phone className={`w-4 h-4 ${variant !== "icon" ? "mr-1" : ""}`} />
          {variant !== "icon" && (label || "Call")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="bg-white border-[#F4F1EA] rounded-xl w-48"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem
          onClick={handleCopyNumber}
          className="cursor-pointer rounded-lg"
          data-testid={testId ? `${testId}-copy` : undefined}
        >
          <Copy className="w-4 h-4 mr-2 text-[#6D6F7C]" />
          <span>Copy number</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleDialAnyway}
          className="cursor-pointer rounded-lg"
          data-testid={testId ? `${testId}-dial` : undefined}
        >
          <ExternalLink className="w-4 h-4 mr-2 text-[#6D6F7C]" />
          <span>Dial anyway</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CallButton;
